import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Slider,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ReactMarkdown from 'react-markdown';

interface IAssistantConfig {
    id: string;
    name: string;
    model: string;
    temperature: number;
    maxTokens: number;
    topP: number;
    frequencyPenalty: number;
    presencePenalty: number;
    responseFormat: string;
    stream: boolean;
    description: string;
    instructions: string;
}

interface IAssistantProps {
    apiKey: string;
}

const models = ['gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo', 'gpt-3.5-turbo-instruct'];
const responseFormats = ['text', 'json_object', 'json_schema'];

const Assistant: React.FC<IAssistantProps> = ({ apiKey }) => {
    const [actionExecuted, setActionExecuted] = useState(false);
    const [assistantsList, setAssistantsList] = useState<{ name: string; id: string }[]>([]);
    // const [models, setModels] = useState<string[]>([]); //not use
    const [model, setModel] = useState(models[0]);

    const [currentAssistant, setCurrentAssistant] = useState<IAssistantConfig>({
        id: '',
        name: '',
        model: '',
        temperature: 1,
        maxTokens: 2048,
        topP: 1,
        frequencyPenalty: 0,
        presencePenalty: 0,
        responseFormat: responseFormats[0],
        stream: true,
        description: '',
        instructions: '',
    });
    const [threadId, setThreadId] = useState<string | null>(null);
    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState('No response available.');
    const [isLoading, setIsLoading] = useState(false);
    const [isFileSearchOpen, setIsFileSearchOpen] = useState(false);
    const [isCodeInterpreterOpen, setIsCodeInterpreterOpen] = useState(false);
    const [isNewAssistant, setIsNewAssistant] = useState(false);
    const [additionalInstructions, setAdditionalInstructions] = useState('');
    const [metadata, setMetadata] = useState<{ [key: string]: string }>({});
    const [fileIds, setFileIds] = useState<string[]>([]);
    const [runId, setRunId] = useState<string | null>(null);

    const [osType, setOsType] = useState<'mac' | 'windows' | 'other'>('other');

    // Load assistants list from API
    const loadAssistants = async () => {
        try {
            const res = await fetch('https://api.openai.com/v1/assistants', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                    'OpenAI-Beta': 'assistants=v2'
                },
            });
            const data = await res.json();
            if (data && data.data) {
                setAssistantsList(data.data.map((assistant: { name: string; id: string }) => ({
                    name: assistant.name,
                    id: assistant.id
                })));
            }
        } catch (error) {
            console.error('Error loading assistants:', error);
        }
    };

    // Load models list from API
    // const loadModels = async () => {
    //     try {
    //         const res = await fetch('https://api.openai.com/v1/models', {
    //             method: 'GET',
    //             headers: {
    //                 'Authorization': `Bearer ${apiKey}`,
    //                 'OpenAI-Beta': 'assistants=v2'
    //             },
    //         });
    //         const data = await res.json();
    //         if (data && data.data) {
    //             const gptModels = data.data
    //                 .filter((model: { id: string }) => model.id.startsWith('gpt')) // Filtra apenas os modelos que começam com "gpt"
    //                 .sort((a: { id: string }, b: { id: string }) => b.id.localeCompare(a.id)) // Ordena os modelos em ordem decrescente
    //                 .map((model: { id: string }) => model.id);
    //             setModels(gptModels);
    //         }
    //     } catch (error) {
    //         console.error('Error loading models:', error);
    //     }
    // };


    // Initialize a new Thread for each interaction
    const initializeThread = async () => {
        try {
            const res = await fetch('https://api.openai.com/v1/threads', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                    'OpenAI-Beta': 'assistants=v2'
                }
            });
            const data = await res.json();

            if (data && data.id) {
                setThreadId(data.id);
                return data.id;
            } else {
                console.error('Failed to create thread');
                setResponse('Error initializing thread.');
                return null;
            }
        } catch (error) {
            console.error('Error initializing thread:', error);
            setResponse('Error initializing thread.');
            return null;
        }
    };

    useEffect(() => {
        loadAssistants();
        // loadModels();
    }, []);

    // Send user message to Thread
    const sendMessage = async (content: string, thread: string) => {
        try {
            const res = await fetch(`https://api.openai.com/v1/threads/${thread}/messages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                    'OpenAI-Beta': 'assistants=v2'
                },
                body: JSON.stringify({
                    role: 'user',
                    content
                })
            });
            const data = await res.json();

            if (data && data.id) {
                console.log('Message sent successfully:', data);
            } else {
                console.error('Failed to send message');
                setResponse('Error sending message.');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setResponse('Error sending message.');
        }
    };

    // Start a Run to process the Thread
    const startRun = async (thread: string) => {
        if (!thread || !currentAssistant.id) return;
        try {
            const res = await fetch(`https://api.openai.com/v1/threads/${thread}/runs`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                    'OpenAI-Beta': 'assistants=v2'
                },
                body: JSON.stringify({
                    assistant_id: currentAssistant.id,
                    // assistant_id: 'asst_T4jmNTBrM3HQi2oDVwVjDw0x',
                    model: currentAssistant.model,
                    temperature: currentAssistant.temperature,
                    top_p: currentAssistant.topP,
                    response_format: 'auto',
                    parallel_tool_calls: true,
                    metadata,
                    additional_instructions: additionalInstructions,
                    // tool_resources: {
                    //     file_search: { file_ids: fileIds },
                    //     code_interpreter: { file_ids: fileIds }
                    // }
                })
            });
            const data = await res.json();
            if (data && data.id) {
                setRunId(data.id);
                monitorRun(thread); // Start monitoring the Run progress
            } else {
                console.error('Failed to start run');
                setResponse('Error starting run.');
            }
        } catch (error) {
            console.error('Error starting run:', error);
            setResponse('Error starting run.');
        }
    };

    // Monitor Run progress
    const monitorRun = async (thread: string) => {
        setIsLoading(true);
        const pollingInterval = 2000;
        const timeoutLimit = 60000;
        let timeElapsed = 0;
        let accumulatedResponse = '';
        const lastMessageIds = new Set<string>();

        try {
            while (timeElapsed < timeoutLimit) {
                const response = await fetch(`https://api.openai.com/v1/threads/${thread}/messages`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${apiKey}`,
                        'Content-Type': 'application/json',
                        'OpenAI-Beta': 'assistants=v2'
                    }
                });
                const data = await response.json();

                if (data && data.data) {
                    // Filtra mensagens novas do assistente que contêm conteúdo
                    const newMessages = data.data.filter((msg: any) =>
                        msg.role === 'assistant' && !lastMessageIds.has(msg.id) && msg.content && msg.content.length > 0
                    );

                    // Adiciona IDs das novas mensagens para evitar duplicação
                    newMessages.forEach((msg: any) => lastMessageIds.add(msg.id));

                    // Acumula e exibe o conteúdo das novas mensagens
                    newMessages.forEach((msg: any) => {
                        if (msg.content[0]?.text?.value) {
                            accumulatedResponse += msg.content[0].text.value;
                            setResponse(accumulatedResponse);
                        }
                    });

                    // Verifica se já temos uma resposta completa (a última mensagem possui o conteúdo final)
                    const isComplete = newMessages.some((msg: any) =>
                        msg.content[0]?.text?.value && msg.content[0].text.value.trim() !== ''
                    );

                    if (isComplete) break; // Sai do loop se a resposta completa for recebida
                }

                await new Promise(resolve => setTimeout(resolve, pollingInterval));
                timeElapsed += pollingInterval;
            }

            if (!accumulatedResponse) {
                setResponse('No response available or request timed out.');
            }
        } catch (error) {
            console.error('Error monitoring the Run:', error);
            setResponse('Error generating text.');
        } finally {
            setIsLoading(false);
        }
    };

    const cancelRun = async () => {
        if (!runId) return;
        await fetch(`https://api.openai.com/v1/threads/${threadId}/runs/${runId}/cancel`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${apiKey}`,
                'OpenAI-Beta': 'assistants=v2'
            }
        });
        setIsLoading(false);
        setResponse('Run cancelled.');
        setRunId(null);
    };

    const handleGenerate = async () => {
        setIsLoading(true);
        setResponse('');

        try {
            const thread = await initializeThread(); // Initialize a new Thread and get its ID
            if (!thread) return; // Stop if thread creation fails

            await sendMessage(prompt, thread); // Send user's prompt as a message to the thread
            await startRun(thread); // Start a Run to process the Thread
        } catch (error) {
            console.error('Error calling OpenAI API:', error);
            setResponse('Error generating text.');
        }
    };

    const handleAssistantChange = (event: SelectChangeEvent<string>) => {
        const selectedAssistant = assistantsList.find(assistant => assistant.id === event.target.value);
        if (selectedAssistant) {
            setCurrentAssistant(prev => ({
                ...prev,
                name: selectedAssistant.name,
                id: selectedAssistant.id
            }));
        }
    };

    const handleModelChange = (event: SelectChangeEvent<string>) => {
        setCurrentAssistant(prev => ({ ...prev, model: event.target.value as string }));
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch('https://api.openai.com/v1/files', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'OpenAI-Beta': 'assistants=v2'
                },
                body: formData
            });
            const data = await res.json();
            setFileIds(prev => [...prev, data.id]);
        }
    };

    return (
        <React.Fragment>
            <Typography variant='h5' sx={{ mb: 2 }}>Chat Assistant</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                    <TextField
                        label='Enter your prompt'
                        multiline
                        rows={4}
                        variant='outlined'
                        fullWidth
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <Button
                        variant='contained'
                        color='primary'
                        onClick={handleGenerate}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} color='inherit' /> : null}
                    >
                        {isLoading ? 'Generating...' : 'Submit'}
                    </Button>
                    
                    {isLoading && (
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={cancelRun}
                            sx={{ ml: 2 }}
                        >
                            Cancel Run
                        </Button>
                    )}

                    <ReactMarkdown>{response}</ReactMarkdown>

                    {/* <TextFieldReadOnly
                        id='response'
                        label='Response'
                        value={response}
                        hasDivider={false}
                        hasToolTip={true}
                        noWrap
                    /> */}
                </Grid>
                <Grid item xs={12} md={2}>
                    {!isNewAssistant ? (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Select Assistant</InputLabel>
                            <Select value={currentAssistant.id || ''} onChange={handleAssistantChange} size='small'>
                                {assistantsList.map((assistant, index) => (
                                    <MenuItem key={index} value={assistant.id}>{assistant.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <>
                            <TextField
                                label="New Assistant Name"
                                value={currentAssistant.name}
                                onChange={(e) => setCurrentAssistant(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Description"
                                value={currentAssistant.description || ''}
                                onChange={(e) => setCurrentAssistant(prev => ({ ...prev, description: e.target.value }))}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Instructions"
                                value={currentAssistant.instructions || ''}
                                onChange={(e) => setCurrentAssistant(prev => ({ ...prev, instructions: e.target.value }))}
                                fullWidth
                                sx={{ mb: 2 }}
                                multiline
                                rows={4}
                            />
                        </>
                    )}
                    {/* <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setIsNewAssistant(!isNewAssistant)}
                        sx={{ mb: 2 }}
                    >
                        {isNewAssistant ? 'Use Existing Assistant' : 'New Assistant'}
                    </Button> */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Select Model</InputLabel>
                        <Select value={currentAssistant.model} onChange={handleModelChange} size='small'>
                            {models.map((modelOption, index) => (
                                <MenuItem key={index} value={modelOption}>{modelOption}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography gutterBottom>Temperature: {currentAssistant.temperature}</Typography>
                    <Slider
                        value={currentAssistant.temperature}
                        min={0}
                        max={1}
                        step={0.1}
                        onChange={(e, newValue) => setCurrentAssistant(prev => ({ ...prev, temperature: newValue as number }))}
                        sx={{ mb: 2 }}
                        size='small'
                    />
                    <Typography gutterBottom>Max Tokens: {currentAssistant.maxTokens}</Typography>
                    <Slider
                        value={currentAssistant.maxTokens}
                        min={1}
                        max={4000}
                        step={50}
                        onChange={(e, newValue) => setCurrentAssistant(prev => ({ ...prev, maxTokens: newValue as number }))}
                        sx={{ mb: 2 }}
                        size='small'
                    />
                    <Typography gutterBottom>Top P: {currentAssistant.topP}</Typography>
                    <Slider
                        value={currentAssistant.topP}
                        min={0}
                        max={1}
                        step={0.1}
                        onChange={(e, newValue) => setCurrentAssistant(prev => ({ ...prev, topP: newValue as number }))}
                        sx={{ mb: 2 }}
                        size='small'
                    />
                    <Typography gutterBottom>Frequency Penalty: {currentAssistant.frequencyPenalty}</Typography>
                    <Slider
                        value={currentAssistant.frequencyPenalty}
                        min={0}
                        max={2}
                        step={0.1}
                        onChange={(e, newValue) => setCurrentAssistant(prev => ({ ...prev, frequencyPenalty: newValue as number }))}
                        sx={{ mb: 2 }}
                        size='small'
                    />
                    <Typography gutterBottom>Presence Penalty: {currentAssistant.presencePenalty}</Typography>
                    <Slider
                        value={currentAssistant.presencePenalty}
                        min={0}
                        max={2}
                        step={0.1}
                        onChange={(e, newValue) => setCurrentAssistant(prev => ({ ...prev, presencePenalty: newValue as number }))}
                        sx={{ mb: 2 }}
                        size='small'
                    />
                    {/* <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Button variant='contained' size='small' onClick={() => setIsFileSearchOpen(true)}>File Search</Button>
                        <Button variant='contained' size='small' onClick={() => setIsCodeInterpreterOpen(true)}>Code Interpreter</Button>
                    </Box> */}

                    <Dialog open={isFileSearchOpen} onClose={() => setIsFileSearchOpen(false)}>
                        <DialogTitle>Upload File - File Search</DialogTitle>
                        <DialogContent>
                            <input type='file' onChange={(e) => handleFileUpload(e)} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsFileSearchOpen(false)}>Cancel</Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog open={isCodeInterpreterOpen} onClose={() => setIsCodeInterpreterOpen(false)}>
                        <DialogTitle>Upload File - Code Interpreter</DialogTitle>
                        <DialogContent>
                            <input type='file' onChange={(e) => handleFileUpload(e)} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsCodeInterpreterOpen(false)}>Cancel</Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default Assistant;
